import { NoteText } from '@/components/mentions/NoteText';
import { NoteComposer } from '@/components/mentions/NoteComposer';
import NoteAttachments from './NoteAttachments';
import axios from 'axios';
import linkifyHtml from 'linkify-html';
import {
  Bot,
  CalendarClock,
  ClipboardList,
  Copy,
  Download,
  Ellipsis,
  Flag,
  HelpCircle,
  Info,
  Loader2,
  Maximize2,
  Pencil,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import moment from 'moment';
import { Component } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import getWebchatUrl from '../../../../utils/getWebchatUrl';
import getTwilioErrorDefinition from '../../../../utils/twilioErrorCodeDefinitions';
import { isScheduledMessageEvent, splitScheduledEventText } from '../scheduled-messages/useScheduledMessages';
import { AiSources } from './AiSources';
import CallCard from './CallCard';
import LongPressArea from './LongPressArea';

const PLACEHOLDER_IMAGE = '/img/image-not-available.png';
const PLACEHOLDER_IMAGE_INSTAGRAM = '/img/story-not-available.png';

const styles = {
  businessImageContainer: {
    padding: '4px 12px 4px 2px',
  },
  customerImageContainer: {
    padding: '4px 2px 4px 12px',
  },
  businessChatBubble: {
    alignSelf: 'flex-end',
    backgroundColor: '#e8f0ff',
    color: 'black',
    borderRadius: '12px 12px 2px 12px',
    marginTop: 1,
    marginRight: 12,
    marginBottom: 1,
    marginLeft: 'auto', // for business this
    maxWidth: 720,
    padding: '5px',
    width: '-webkit-fit-content',
  },
  customerChatBubble: {
    alignSelf: 'flex-end',
    backgroundColor: '#eeeeee',
    color: 'black',
    borderRadius: '12px 12px 12px 2px',
    marginTop: 1,
    marginRight: 'auto', // for customer
    marginBottom: 1,
    marginLeft: 8,
    maxWidth: 720,
    padding: '5px',
    width: '-webkit-fit-content',
  },
  eventChatBubble: {
    alignSelf: 'center',
    backgroundColor: 'rgba(0,0,0,0)',
    color: '#999',
    borderRadius: 15,
    marginTop: 1,
    marginRight: 'auto', // for event
    marginBottom: 1,
    marginLeft: 'auto', // for event
    maxWidth: 800,
    padding: '4px 7px',
    width: '-webkit-fit-content',
  },
  businessImage: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  customerImage: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
};

class ChatBubble extends Component {
  state = {
    dialogOpen: false,
    hovering: false,
    showSeeMore: false,
    isEditing: false,
    editText: '',
    savingEdit: false,
    confirmRemoveOpen: false,
    eventExpanded: false,
  };

  renderScheduledEvent = (text) => {
    const { label, preview } = splitScheduledEventText(text);
    const expanded = this.state.eventExpanded;
    return (
      <button
        type="button"
        title={expanded ? undefined : text}
        aria-expanded={preview ? expanded : undefined}
        aria-label={preview ? `${label}: ${preview}` : label}
        onClick={() => preview && this.setState({ eventExpanded: !expanded })}
        className={cn(
          'flex min-w-0 max-w-full items-start gap-2 border border-border/60 bg-muted/40 px-3 py-1 text-left text-xs leading-4 text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          expanded ? 'max-w-md rounded-xl' : 'rounded-full',
          !preview && 'cursor-default',
        )}
      >
        <CalendarClock className="size-3.5 shrink-0" aria-hidden />
        <span className={cn('min-w-0', expanded ? 'whitespace-pre-wrap break-words' : 'truncate')}>
          <span className="font-medium text-foreground/80">{label}</span>
          {preview && ` · ${expanded ? preview : preview.replace(/\s+/g, ' ')}`}
        </span>
      </button>
    );
  };

  // Only the note's author can edit, and only persisted (non-optimistic) notes.
  canEditNote = () =>
    this.props.sender === 'note' &&
    Boolean(this.props._user) &&
    Boolean(this.props.currentUserId) &&
    this.props._user === this.props.currentUserId &&
    this.props.status !== 'sending' &&
    this.props.status !== 'failed';

  isNoteEdited = () => {
    const { sender, updatedAt, createdAt } = this.props;
    if (sender !== 'note' || !updatedAt || !createdAt) return false;
    return new Date(updatedAt).getTime() - new Date(createdAt).getTime() > 1000;
  };

  startEditingNote = () => {
    this.setState(
      {
        isEditing: true,
        editText: this.props.text || '',
        showSeeMore: false,
      },
      // Phones: Edit note in the sheet goes straight to typing, no second tap.
      // A timeout, not a frame, so iOS still counts the tap and shows the
      // keyboard; by then the closing sheet has let go of the focus.
      () => this.isMobileBubble() && setTimeout(this.focusNoteEditor, 0),
    );
  };

  focusNoteEditor = () => {
    const field = this.noteEditorWrap?.querySelector('[contenteditable="true"]');
    if (!field) return;
    field.focus({ preventScroll: true });
    const range = document.createRange();
    range.selectNodeContents(field);
    range.collapse(false);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  };

  cancelEditingNote = () => {
    this.setState({ isEditing: false, editText: '' });
  };

  saveEditingNote = async () => {
    const text = (this.state.editText || '').trim();
    if (!text || this.state.savingEdit) {
      return;
    }
    if (text === (this.props.text || '').trim()) {
      this.setState({ isEditing: false, editText: '' });
      return;
    }
    this.setState({ savingEdit: true });
    const res = await this.props.onEditNote?.({ _message: this.props._message, text });
    this.setState({ savingEdit: false });
    if (res?.error) {
      toast.error(res.message || 'Could not save your note.');
      return;
    }
    this.setState({ isEditing: false, editText: '' });
  };

  renderNoteEditor = () => (
    <NoteComposer key={this.props._message} draftKey={`edit:${this.props._message}`}
      _customer={this.props._customer} _business={this.props._business}
      initialText={this.props.text} initialMentions={this.props.mentions || []} expectedRevision={this.props.noteRevision || 0}
      onCancel={this.cancelEditingNote} onSave={async (draft) => {
        const result = await this.props.onEditNote?.({ _message: this.props._message, ...draft });
        if (!result?.error) this.cancelEditingNote();
        return result;
      }} />
  );

  isOutboundSender = () =>
    this.props.sender === 'business' || this.props.sender === 'ai' || this.props.sender === 'note';

  constructor(props) {
    super(props);
    this.webchatApiUrl = getWebchatUrl(window.location.href);
  }

  componentDidMount() {
    this.fetchContentType();
  }

  fetchContentType = async () => {
    try {
      if (!this.props.media?.includes('ig_messaging')) {
        return;
      }

      if (this.props.media.includes('ig_messaging')) {
        const res = await axios.post(`${this.webchatApiUrl}/api/instagram-content-type`, {
          mediaUrl: this.props.media,
        });
        const { contentType } = res.data;

        if (contentType.includes('video')) {
          this.setState({ contentType: 'video' });
        } else if (contentType.includes('image')) {
          this.setState({ contentType: 'image' });
        }
      }
    } catch {
      // console.log(err);
    }
  };

  // Sources whose information appears in the AI response. The draft card in
  // the composer shows the same list, so it lives in AiSources.
  renderAiSources = () => <AiSources aiMeta={this.props.aiMeta} />;

  // Phones: there is no hover, so a tap or a long press on the bubble opens
  // its actions in one ActionSheet that ChatFeed owns.
  isMobileBubble = () => Boolean(this.props.isMobile && this.props.onShowActions);

  hasRemovePermission = () => {
    const { accessLevel } = this.props;
    return accessLevel === 'owner' || accessLevel === 'location-admin' || accessLevel === 'team-leader';
  };

  copyText = async () => {
    try {
      await navigator.clipboard.writeText(this.props.text || '');
      toast.success('Copied', { position: 'top-center', duration: 1500 });
    } catch {
      toast.error('Could not copy this message', { position: 'top-center' });
    }
  };

  getMobileMeta = () => {
    const { sentBy, createdAt, smsStatus, channel } = this.props;
    const via =
      channel === 'email'
        ? 'via email'
        : channel === 'phone'
          ? 'via sms'
          : channel === 'webchat'
            ? 'via website widget'
            : '';
    return [sentBy, moment(createdAt).format('llll'), smsStatus, via].filter(Boolean).join(' · ');
  };

  showMobileActions = (type = 'text') => {
    const { text, media, sender, status, onRestore, questionnaireRequestId, errorCode } = this.props;
    const isFile =
      media && ['.pdf', '.txt', '.csv', '.amr'].some((extension) => media.includes(extension));
    const isPlayable = media && ['.mp3', '.m4a', '.mov', '.mp4'].some((extension) => media.includes(extension));
    const isQuestionnaire = text?.includes('@customer-response');

    const items = [];
    if (status === 'failed' && typeof onRestore === 'function') {
      items.push({ id: 'retry', label: 'Edit and resend', icon: RotateCcw, onSelect: onRestore });
    }
    if (text && !isQuestionnaire) {
      items.push({ id: 'copy', label: 'Copy text', icon: Copy, onSelect: this.copyText });
    }
    if (type === 'media' && media) {
      items.push(
        isFile || isPlayable || this.state.contentType === 'video'
          ? { id: 'open', label: 'Download', icon: Download, onSelect: () => window.open(media, '_blank', 'noopener') }
          : { id: 'open', label: 'View image', icon: Maximize2, onSelect: () => this.props.showImageLightbox?.(media) },
      );
    }
    if (questionnaireRequestId) {
      items.push({
        id: 'responses',
        label: 'View responses',
        icon: ClipboardList,
        onSelect: () => this.props.fetchCustomerFeedback({ _questionnaireRequest: questionnaireRequestId }),
      });
    }
    if (this.canEditNote()) {
      items.push({ id: 'edit', label: 'Edit note', icon: Pencil, onSelect: this.startEditingNote });
    }

    const sections = items.length ? [{ id: 'actions', items }] : [];
    // Same senders as the desktop menu; a bubble still sending has nothing saved to remove.
    if (['business', 'customer', 'ai', 'note'].includes(sender) && status !== 'sending' && status !== 'failed') {
      sections.push({
        id: 'remove',
        items: [
          {
            id: 'remove',
            label: text && media && !isFile ? 'Remove image and text' : 'Remove',
            icon: Trash2,
            destructive: true,
            onSelect: () => this.setState({ confirmRemoveOpen: true }),
          },
        ],
      });
    }

    this.props.onShowActions({
      title: sender === 'note' ? 'Note actions' : 'Message actions',
      header: (
        <div className="mb-3 rounded-xl bg-background px-4 py-3 dark:bg-zinc-900">
          {(text || media || (sender === 'note' && this.props.attachments?.length > 0)) && (
            <p className="line-clamp-4 whitespace-pre-wrap break-words text-[15px] leading-snug">
              {isQuestionnaire ? 'Questionnaire answers submitted' : text || 'Attachment'}
            </p>
          )}
          <p className="mt-1 text-[13px] text-muted-foreground">{this.getMobileMeta()}</p>
          {errorCode && (
            <p className="mt-2 flex items-start gap-1.5 text-[13px] text-red-600 dark:text-red-400">
              <HelpCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
              <span>
                {getTwilioErrorDefinition(errorCode)} ({errorCode})
              </span>
            </p>
          )}
        </div>
      ),
      sections,
    });
  };

  // Tap on a phone bubble: links and buttons inside it keep working.
  handleMobileBubbleTap = (event) => {
    if (event.target.closest?.('a, button, input, textarea, [contenteditable="true"]')) return;
    this.showMobileActions('text');
  };

  renderMobileRemoveDialog = () => {
    const { sender } = this.props;
    const allowed = this.hasRemovePermission();
    return (
      <AlertDialog
        open={this.state.confirmRemoveOpen}
        onOpenChange={(open) => this.setState({ confirmRemoveOpen: open })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{allowed ? 'Remove for you?' : 'Cannot remove message'}</AlertDialogTitle>
            <AlertDialogDescription>
              {allowed
                ? sender === 'note'
                  ? "This note will be removed from your team's dashboard."
                  : "This message will be removed from your team's dashboard. Your customers will still be able to see it."
                : 'You do not have permission to remove this message.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{allowed ? 'Cancel' : 'OK'}</AlertDialogCancel>
            {allowed && (
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={this.removeMessage}
              >
                Remove
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  };

  renderSeeMore = ({ sender, type }) => {
    // The phone bubble opens its actions on tap or long press instead.
    if (this.isMobileBubble()) {
      return null;
    }

    // type is text or media
    const { text, media } = this.props;

    const isPdfTxtOrCsv =
      media && (media.includes('.pdf') || media.includes('.txt') || media.includes('.csv'));

    // The phone sheet's Remove asks the same question (renderMobileRemoveDialog).
    const isValidAccessLevel = this.hasRemovePermission();

    // don't render "see more" buttons on the text if we already have one on the image
    if (type === 'text' && media && !isPdfTxtOrCsv) {
      return <span />;
    }

    if (sender === 'business' || sender === 'customer' || sender === 'ai' || sender === 'note') {
      return (
        <AlertDialog>
          <DropdownMenu
            open={this.state.showSeeMore}
            onOpenChange={() => {
              this.setState({
                showSeeMore: !this.state.showSeeMore,
              });
            }}
          >
            <DropdownMenuTrigger asChild>
              <Button
                className={cn(this.state.hovering ? 'flex' : 'hidden', 'mr-1 rounded-full! p-2!')}
                size="icon"
                variant="ghost"
                onClick={() => {
                  this.setState({ showSeeMore: true });
                }}
              >
                <Ellipsis className={cn('size-4 text-muted-foreground')} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              hideWhenDetached={true}
              onInteractOutside={() => {
                this.setState({
                  showSeeMore: false,
                  hovering: false,
                });
              }}
              onEscapeKeyDown={() => {
                this.setState({
                  showSeeMore: false,
                  hovering: false,
                });
              }}
            >
              {this.canEditNote() && (
                <DropdownMenuItem
                  className="w-full"
                  onSelect={(e) => {
                    e.preventDefault();
                    this.startEditingNote();
                  }}
                >
                  <Pencil className="size-3.5 mr-2" />
                  Edit
                </DropdownMenuItem>
              )}
              <AlertDialogTrigger className="w-full">
                <DropdownMenuItem
                  className="w-full"
                  onSelect={() => {
                    this.setState({
                      // dialogOpen: true,
                      showSeeMore: false, // Reset seeMoreClicked state
                    });
                  }}
                >
                  {text && media && !isPdfTxtOrCsv ? 'Remove Image and Text' : 'Remove'}
                </DropdownMenuItem>
              </AlertDialogTrigger>
            </DropdownMenuContent>
          </DropdownMenu>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {isValidAccessLevel ? 'Remove for you?' : 'Cannot remove message'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {isValidAccessLevel
                  ? sender === 'note'
                    ? "This note will be removed from your team's dashboard."
                    : "This message will be removed from your team's dashboard. Your customers will still be able to see it."
                  : 'You do not have permission to remove this message.'}
              </AlertDialogDescription>
              <AlertDialogFooter>
                <AlertDialogCancel>{isValidAccessLevel ? 'Cancel' : 'OK'}</AlertDialogCancel>
                {isValidAccessLevel && (
                  <AlertDialogAction onClick={this.removeMessage}>Confirm</AlertDialogAction>
                )}
              </AlertDialogFooter>
            </AlertDialogHeader>
          </AlertDialogContent>
        </AlertDialog>
      );
    }

    return <span />;
  };

  renderMessageMedia = () => {
    if (this.props.media) {
      let mediaName = '';
      if (this.props.media.includes('.pdf')) {
        mediaName = 'PDF';
      } else if (this.props.media.includes('.csv')) {
        mediaName = 'CSV';
      } else if (this.props.media.includes('.txt')) {
        mediaName = 'TXT';
      } else if (this.props.media.includes('.amr')) {
        mediaName = 'AMR';
      }

      // render pdf, txt, csv or amr file (browsers can't play amr audio)
      if (
        this.props.media.includes('.pdf') ||
        this.props.media.includes('.txt') ||
        this.props.media.includes('.csv') ||
        this.props.media.includes('.amr')
      ) {
        return (
          <div
            style={
              this.isOutboundSender()
                ? styles.businessImageContainer
                : styles.customerImageContainer
            }
          >
            <div
              className={cn('flex items-center', this.getDirection(this.props.sender))}
              onMouseEnter={this.handleHoverEvent}
              onMouseLeave={this.handleHoverEvent}
            >
              <div>
                <Card style={this.isOutboundSender() ? styles.businessImage : styles.customerImage}>
                  <CardContent className="p-4flex! items-center flex-col justify-center h-full">
                    <div
                      style={{
                        width: 80,
                      }}
                    >
                      <div
                        style={{
                          padding: 2,
                        }}
                        className="truncate text-center text-sm font-semibold text-muted-foreground"
                      >
                        {mediaName ? mediaName : 'File'}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          padding: 2,
                          flexGrow: 1,
                        }}
                        className="flex space-x-1.5"
                      >
                        <Download className="size-5" />
                        <a href={this.props.media}>Download</a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              <div>
                {this.renderSeeMore({
                  sender: this.props.sender,
                  type: 'media',
                })}
              </div>
            </div>
          </div>
        );
      }

      // instagram story video
      if (this.state.contentType === 'video') {
        return (
          <div
            className={cn('flex items-center', this.getDirection(this.props.sender))}
            onMouseEnter={this.handleHoverEvent}
            onMouseLeave={this.handleHoverEvent}
          >
            <div>
              <div
                style={
                  this.isOutboundSender()
                    ? styles.businessImageContainer
                    : styles.customerImageContainer
                }
              >
                <video width="225" height="400" controls className="rounded-xl bg-muted/30 max-md:h-auto max-md:w-[min(60vw,14rem)]">
                  <source src={this.props.media} />
                </video>
              </div>
            </div>
          </div>
        );
      }

      // render audio
      if (this.props.media.includes('.mp3') || this.props.media.includes('.m4a')) {
        return (
          <div
            className={cn('flex items-center', this.getDirection(this.props.sender))}
            onMouseEnter={this.handleHoverEvent}
            onMouseLeave={this.handleHoverEvent}
          >
            <div
              style={
                this.isOutboundSender()
                  ? styles.businessImageContainer
                  : styles.customerImageContainer
              }
            >
              {/* biome-ignore lint/a11y/useMediaCaption: customer voice notes have no captions */}
              <audio controls preload="none" src={this.props.media} className="w-64 max-w-full" />
            </div>
            <div>
              {this.renderSeeMore({
                sender: this.props.sender,
                type: 'media',
              })}
            </div>
          </div>
        );
      }

      // render video
      if (this.props.media.includes('.mov') || this.props.media.includes('.mp4')) {
        return (
          <div
            className={cn('flex items-center', this.getDirection(this.props.sender))}
            onMouseEnter={this.handleHoverEvent}
            onMouseLeave={this.handleHoverEvent}
          >
            <div>
              <div
                style={
                  this.isOutboundSender()
                    ? styles.businessImageContainer
                    : styles.customerImageContainer
                }
              >
                <video width="320" height="240" controls className="rounded-xl bg-muted/30 max-md:h-auto max-md:w-[min(70vw,16rem)]">
                  <source src={this.props.media} />
                </video>
              </div>
            </div>
            <div>
              {this.renderSeeMore({
                sender: this.props.sender,
                type: 'media',
              })}
            </div>
          </div>
        );
      }

      // render image
      return (
        <div
          className={cn('flex items-center', this.getDirection(this.props.sender))}
          onMouseEnter={this.handleHoverEvent}
          onMouseLeave={this.handleHoverEvent}
        >
          <div>
            <div
              style={
                this.isOutboundSender()
                  ? styles.businessImageContainer
                  : styles.customerImageContainer
              }
            >
              <div style={this.isOutboundSender() ? styles.businessImage : styles.customerImage}>
                <img
                  src={this.props.media}
                  alt="Textchat"
                  id="webchat-img"
                  key={this.props.media}
                  onClick={() => this.props.showImageLightbox(this.props.media)}
                  onError={(e) => {
                    if (
                      e.target.src !== PLACEHOLDER_IMAGE ||
                      e.target.src !== PLACEHOLDER_IMAGE_INSTAGRAM
                    ) {
                      e.target.onerror = null;
                      e.target.src = e.target.src.includes('ig_messaging')
                        ? PLACEHOLDER_IMAGE_INSTAGRAM
                        : PLACEHOLDER_IMAGE;
                    }
                  }}
                  style={{
                    cursor: 'zoom-in',
                    minHeight: 100,
                  }}
                  className="rounded-xl xs:max-w-32 sm:max-w-64 lg:max-w-md max-w-full bg-muted/30 max-md:max-h-80 max-md:max-w-[min(70vw,16rem)] max-md:object-cover"
                />
              </div>
            </div>
          </div>
          <div>
            {this.renderSeeMore({
              sender: this.props.sender,
              type: 'media',
            })}
          </div>
        </div>
      );
    }
    return null;
  };

  chekkitUrl = 'dashboard.chekkit.io';

  renderText = ({ text, sender }) => {
    if (!text) {
      return null;
    }

    // On the blue AI bubble the default blue link color blends into the
    // background, so use a high-contrast white underlined link instead.
    const linkClassName =
      sender === 'ai'
        ? 'linkified text-white underline underline-offset-2 font-medium break-all'
        : 'linkified text-blue-500 break-all';

    // render questionnaire response as a button
    if (text.includes('@customer-response')) {
      const _questionnaireRequest = text.split('_questionnaireRequest=')[1];

      return (
        <div className="flex flex-col items-center gap-1.5">
          <p className="text-sm font-medium text-foreground">
            Questionnaire answers submitted
          </p>
          <Button
            key={`link${_questionnaireRequest}`}
            variant="outline"
            size="sm"
            className="h-7 px-2.5 text-xs"
            onClick={() =>
              this.props.fetchCustomerFeedback({
                _questionnaireRequest,
              })
            }
          >
            View responses
          </Button>
        </div>
      );
    }

    // Helper function to escape HTML entities
    const escapeHtml = (text) => {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    };

    const formatText = (inputText) => {
      try {
        // First escape HTML entities to prevent HTML injection
        const escapedText = escapeHtml(inputText);

        // Use linkifyHtml to convert URLs to <a> tags
        const formattedText = linkifyHtml(escapedText, {
          target: '_blank',
          rel: 'noopener noreferrer',
          className: linkClassName,
        });

        return { __html: formattedText };
      } catch (error) {
        console.warn('linkifyHtml error:', error);
        // Fallback: Simple URL detection and replacement
        // First escape HTML entities
        const escapedText = escapeHtml(inputText);
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const formattedText = escapedText.replace(urlRegex, (url) => {
          return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="${linkClassName}">${url}</a>`;
        });

        return { __html: formattedText };
      }
    };

    return (
      <div
        dangerouslySetInnerHTML={formatText(text)}
        style={{
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
        }}
      />
    );
  };

  getChatBubbleStyle = (sender) => {
    const baseBubbleStyle = 'flex gap-2 flex-col px-3.5 py-2 font-normal leading-relaxed';
    switch (sender) {
      case 'business':
        return `${baseBubbleStyle} rounded-[18px_18px_5px_18px] bg-bubble ml-auto mr-3 my-0.5 text-[15px]`;
      case 'ai':
        return (
          baseBubbleStyle +
          ' rounded-[18px_18px_5px_18px] bg-blue-600 dark:bg-blue-700 ml-auto mr-3 my-0.5 text-[15px]'
        );
      case 'customer':
        return `${baseBubbleStyle} rounded-[18px_18px_18px_5px] border border-border/70 bg-muted/65 mr-auto ml-3 my-0.5 text-[15px]`;
      case 'note':
        return (
          baseBubbleStyle +
          ' rounded-[18px_18px_5px_18px] bg-yellow-100/70 dark:bg-yellow-900/40 border border-yellow-200/70 dark:border-yellow-800/60 ml-auto mr-3 my-0.5 text-[15px]'
        );
      case 'event':
        return 'flex items-center gap-3 mx-auto self-center text-[11px] text-muted-foreground/60 italic max-w-fit px-3 py-2';
      default:
        return `${baseBubbleStyle} rounded-[18px_18px_5px_18px] bg-bubble ml-auto mr-3 my-0.5 text-[15px]`;
    }
  };

  getChatBubbleContainerStyle = (sender) => {
    const chatBubbleContainer = 'flex flex-col whitespace-pre-line';

    switch (sender) {
      case 'business':
      case 'ai':
      case 'note':
        return `${chatBubbleContainer} justify-end`;
      case 'customer':
        return `${chatBubbleContainer} justify-start`;
      case 'event':
        return `${chatBubbleContainer} justify-center`;
      default:
        return `${chatBubbleContainer} justify-end`;
    }
  };

  getChatBubbleTextColor = (sender) => {
    switch (sender) {
      case 'ai':
        return 'text-white';
      default:
        return 'inherit';
    }
  };

  getDirection = (sender) => {
    if (sender === 'business' || sender === 'ai' || sender === 'note') {
      return 'flex-row-reverse';
    }

    return 'flex-row';
  };

  getJustify = (sender) => {
    if (sender === 'event') {
      return 'justify-center';
    }

    return 'justify-start';
  };

  handleHoverEvent = (event) => {
    if (event.type === 'mouseenter') {
      this.setState({ hovering: true });
    }
    if (!this.state.showSeeMore) {
      if (event.type === 'mouseleave' || event.type === 'mouseout') {
        this.setState({ hovering: false });
      }
    }
  };

  closeDialog = () => {
    this.setState({ dialogOpen: false });
  };

  removeMessage = () => {
    this.props.handleConfirmRemoveMessageClick({
      _message: this.props._message,
    });
    this.setState({ dialogOpen: false });
  };

  render() {
    const { text, createdAt, sender, linkedWorkflow, _call, status, onRestore } = this.props;
    const isSending = status === 'sending';
    const isFailed = status === 'failed';
    const isRestorable = isFailed && typeof onRestore === 'function';

    // Render CallCard if this is a call event message
    if (_call && sender === 'event') {
      return <CallCard call={_call} createdAt={createdAt} />;
    }

    const handleBubbleClick = () => {
      if (isRestorable) onRestore();
    };

    const mobile = this.isMobileBubble();
    // Notes can carry photos and files, with or without text.
    const noteAttachments = sender === 'note' ? this.props.attachments || [] : [];
    const hasBubble = Boolean(text) || noteAttachments.length > 0;
    // Keyboard and VoiceOver reach the phone actions the same way a tap does.
    const keyboardAction = isRestorable
      ? handleBubbleClick
      : mobile
        ? () => this.showMobileActions('text')
        : null;

    const textBubble = hasBubble ? (
      <div
        className={cn(
          this.getChatBubbleStyle(sender),
          isSending && 'opacity-60',
          isFailed && 'ring-1 ring-red-500/60',
          isRestorable && 'cursor-pointer',
        )}
        onClick={isRestorable ? handleBubbleClick : mobile ? this.handleMobileBubbleTap : undefined}
        role={isRestorable || mobile ? 'button' : undefined}
        tabIndex={isRestorable || mobile ? 0 : undefined}
        aria-haspopup={mobile && !isRestorable ? 'dialog' : undefined}
        onKeyDown={
          keyboardAction
            ? (e) => {
                if (mobile && e.target !== e.currentTarget) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  keyboardAction();
                }
              }
            : undefined
        }
      >
        {sender === 'ai' && (
          <span className="text-[10px] font-medium text-blue-100 flex items-center gap-1">
            <Bot className="size-3" />
            AI Employee
          </span>
        )}

        {text && (
          <div className={cn(this.getChatBubbleTextColor(sender), 'break-words')}>
            {sender === 'note' ? <NoteText text={text} mentions={this.props.mentions} /> : this.renderText({ text, sender })}
          </div>
        )}
        {noteAttachments.length > 0 && (
          <NoteAttachments attachments={noteAttachments} showImageLightbox={this.props.showImageLightbox} />
        )}
        {sender === 'ai' && this.renderAiSources()}

        {this.props.questionnaireRequestId && (
          <div className="flex pt-1">
            <Button
              key={`link${this.props.questionnaireRequestId}`}
              variant="outline"
              size="sm"
              className="h-7 px-2.5 text-xs max-md:h-9"
              onClick={() =>
                this.props.fetchCustomerFeedback({
                  _questionnaireRequest: this.props.questionnaireRequestId,
                })
              }
            >
              View responses
            </Button>
          </div>
        )}
      </div>
    ) : null;

    return (
      <div className="py-1.5">
        {mobile && this.props.media ? (
          <LongPressArea onLongPress={() => this.showMobileActions('media')}>
            {this.renderMessageMedia()}
          </LongPressArea>
        ) : (
          this.renderMessageMedia()
        )}
        <div
          className={cn(this.getChatBubbleContainerStyle(sender))}
          onMouseEnter={this.handleHoverEvent}
          onMouseLeave={this.handleHoverEvent}
        >
          <div
            className={cn(
              'flex items-center space-x-1',
              this.getDirection(sender),
              this.getJustify(sender),
            )}
          >
            <div className={sender === 'event' ? 'w-full' : 'max-w-[80%]'}>
              {text && sender === 'event' ? (
                text.includes('@customer-response') ? (
                  <div className="my-2 flex w-full justify-center px-4">
                    <div className="min-w-0 break-words text-sm text-muted-foreground">
                      {sender === 'note' ? <NoteText text={text} mentions={this.props.mentions} /> : this.renderText({ text, sender })}
                    </div>
                  </div>
                ) : (
                  <div className="my-2 flex w-full justify-center px-4">
                    {isScheduledMessageEvent(text)
                      ? this.renderScheduledEvent(text)
                      : (
                        <div className="flex min-w-0 max-w-full items-start gap-2 text-xs leading-4 text-muted-foreground">
                          <Info className="size-3.5 shrink-0" aria-hidden />
                          <div className="min-w-0 break-words">{this.renderText({ text, sender })}</div>
                        </div>
                      )}
                  </div>
                )
              ) : sender === 'note' && this.state.isEditing ? (
                <div
                  ref={(node) => {
                    this.noteEditorWrap = node;
                  }}
                  className={cn(this.getChatBubbleStyle(sender), mobile && 'max-md:[&_button]:min-h-11')}
                >
                  {this.renderNoteEditor()}
                </div>
              ) : hasBubble ? (
                mobile ? (
                  <LongPressArea onLongPress={() => this.showMobileActions('text')}>{textBubble}</LongPressArea>
                ) : (
                  textBubble
                )
              ) : null}
              {/* linkedWorkflow is a string like "quote/123" or "job/123" */}
              {linkedWorkflow && !linkedWorkflow.includes('opportunity') && (
                <div className="flex justify-start px-3 pt-1 pb-1">
                  <Button
                    asChild
                    // variant="outline"
                    size="sm"
                  >
                    <Link to={`/work/view/${linkedWorkflow}`}>
                      <Flag className="size-4 mr-1" />
                      View requested changes
                    </Link>
                  </Button>
                </div>
              )}
            </div>
            {!mobile && (
              <div className="max-w-[10%]">
                {hasBubble && this.renderSeeMore({ sender, type: 'text' })}
              </div>
            )}
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: sender === 'customer' ? 'flex-start' : 'flex-end',
            }}
            className="px-4 pb-1"
          >
            <div
              className={cn('text-[11px] leading-4', isFailed ? 'text-red-500' : 'text-muted-foreground')}
              style={{
                wordBreak: 'break-word',
              }}
            >
              {sender !== 'event' &&
                (isFailed ? (
                  <span>{mobile ? 'Not delivered · Tap to retry' : 'Not delivered · Click to retry'}</span>
                ) : isSending ? (
                  <span>Sending…</span>
                ) : (
                  <span className="inline-flex items-center gap-1">
                    {`${this.props.sentBy} ${moment(createdAt).format('LT')}`}
                    {this.isNoteEdited() && (
                      <span className="text-muted-foreground/70">(edited)</span>
                    )}
                    {this.props.smsStatus && (
                      <span className={this.props.errorCode ? 'text-red-500' : ''}>
                        {` - ${this.props.smsStatus}`}
                      </span>
                    )}
                    {`${
                      this.props.channel === 'email'
                        ? ' via email'
                        : this.props.channel === 'phone'
                          ? ' via sms'
                          : this.props.channel === 'webchat'
                            ? ' via website widget'
                            : ''
                    }`}
                    {this.props.errorCode && mobile && (
                      <button
                        type="button"
                        aria-label="Why this message was not delivered"
                        onClick={() => this.showMobileActions('text')}
                        className="-mx-3 -my-3.5 inline-flex size-11 items-center justify-center"
                      >
                        <HelpCircle className="size-3.5" strokeWidth={2.5} />
                      </button>
                    )}
                    {this.props.errorCode && !mobile && (
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="size-3 cursor-pointer" strokeWidth={2.5} />
                          </TooltipTrigger>
                          <TooltipContent>
                            {getTwilioErrorDefinition(this.props.errorCode)} ({this.props.errorCode}
                            )
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </span>
                ))}
              {/* Phones: an attachment's actions (view, download, remove) are
              on a button too, not only on a long press of the media. */}
              {mobile && this.props.media && sender !== 'event' && !isSending && (
                <button
                  type="button"
                  aria-label="Attachment actions"
                  aria-haspopup="dialog"
                  onClick={() => this.showMobileActions('media')}
                  className="-my-3.5 ml-0.5 -mr-3 inline-flex size-11 items-center justify-center align-middle text-muted-foreground"
                >
                  <Ellipsis className="size-4" aria-hidden />
                </button>
              )}
            </div>
          </div>
        </div>
        {mobile && this.renderMobileRemoveDialog()}
      </div>
    );
  }
}

export default ChatBubble;
